/**
 * Asset Aliases Service
 *
 * HTTP wrappers for the caller's own ticker→asset rules
 * (`bat.tenant_asset_aliases`), written whenever they resolve an unknown ticker
 * from the "Needs attention" tray.
 *
 * Endpoints (tenant + user scoped server-side):
 *   GET    /api/assets/aliases
 *   DELETE /api/assets/aliases/:aliasId
 *
 * Contract types live in `@bat/shared` (single source of truth). Lives under the
 * import feature because the panel that consumes it sits on /import next to the
 * tray that CREATES these rows.
 */

import type { ListTenantAssetAliasesResponse, DeleteTenantAssetAliasResponse } from '@bat/shared'

import { api, throwBackendError } from '@/platform/api/api.service'
import { ASSET_ROUTES } from '@/shared/constants/config-routes'

/**
 * List the caller's aliases. Throws via `throwBackendError` on
 * `{ success: false }` so React Query surfaces `isError` instead of rendering
 * an empty list over a failed fetch.
 */
export const listAssetAliases = async (): Promise<ListTenantAssetAliasesResponse> => {
  const response = await api.get<ListTenantAssetAliasesResponse>(ASSET_ROUTES.ALIASES)

  if (!response.success || !response.data) {
    throwBackendError(response, 'ASSET_ALIASES_LIST_FAILED')
  }

  return response.data
}

/**
 * Delete ONE alias by id. The backend answers 404 when the alias is not the
 * caller's, so a stale row in an old render fails loudly rather than silently
 * removing somebody else's rule.
 */
export const deleteAssetAlias = async (
  aliasId: string,
): Promise<DeleteTenantAssetAliasResponse> => {
  const response = await api.delete<DeleteTenantAssetAliasResponse>(
    ASSET_ROUTES.DELETE_ALIAS(aliasId),
  )

  if (!response.success || !response.data) {
    throwBackendError(response, 'ASSET_ALIAS_DELETE_FAILED')
  }

  return response.data
}
